import axios from 'axios'

/**
 * 教师网页端 · 接口封装（替代小程序 pages/teacher_login、education_* 系列）
 *
 * 后端：dt6 的 wx_teacher 模块
 *   登录：/wx_teacher/web/login       （新增，手机号 + 密码）
 *   教务：/wx_teacher/education/*     （小程序原接口，鉴权仍是 tele + wx_login_key）
 *
 * 地址切换（环境自动区分，不用手动改）：
 *   本地开发：走 vue.config.js 的 /api 代理 → https://www.ysdeyu.cn/dt6/public/index.php
 *   线上部署：同域名直连完整路径（无需 nginx 配 /api 转发，与评价模块 eval.js 一致）
 */
export const TEACHER_API = process.env.NODE_ENV === 'development'
  ? '/api'
  : 'https://www.ysdeyu.cn/dt6/public/index.php'

const TOKEN_KEY = 'teacher_login_info'

/** 读取本地登录信息 {token, tele, name} */
export function getTeacherLogin() {
  try {
    return JSON.parse(localStorage.getItem(TOKEN_KEY) || '{}')
  } catch (e) {
    return {}
  }
}

export function setTeacherLogin(info) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(info || {}))
}

export function clearTeacherLogin() {
  localStorage.removeItem(TOKEN_KEY)
}

const http = axios.create({
  baseURL: TEACHER_API,
  timeout: 60000
})

// 统一带上 tele + wx_login_key（后端 wx_teacher_check 中间件就认这两个参数）
http.interceptors.request.use((config) => {
  const info = getTeacherLogin()
  if (info.token) {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.data.append('tele', info.tele || '')
      config.data.append('wx_login_key', info.token)
    } else {
      config.data = Object.assign({}, config.data, {
        tele: info.tele || '',
        wx_login_key: info.token
      })
    }
  }
  return config
})

// 401 统一回登录页
http.interceptors.response.use(
  (respon) => respon,
  (error) => {
    if (error.response && error.response.status === 401) {
      clearTeacherLogin()
      window.location.hash = '#/tlogin'
    }
    return Promise.reject(error)
  }
)

/** 登录：手机号 + 密码 */
export const teacherLogin = (data) => http.post('/wx_teacher/web/login', data).then((r) => r.data)

/** 当前教师信息（含教务管理权限） */
export const teacherProfile = () => http.post('/wx_teacher/web/profile', {}).then((r) => r.data)

/** 修改密码 */
export const teacherChangePwd = (data) => http.post('/wx_teacher/web/change_password', data).then((r) => r.data)

/* ---------------- 教务管理（查课登记） ---------------- */

/** 星期列表 */
export const eduWeeks = () => http.post('/wx_teacher/education/get_week', {}).then((r) => r.data)

/** 节次列表（含上下课时间） */
export const eduSections = () => http.post('/wx_teacher/education/get_section', {}).then((r) => r.data)

/** 班级列表（含年级） */
export const eduClassList = () => http.post('/wx_teacher/education/class_list', {}).then((r) => r.data)

/** 教师名单（查课登记的教师只能从这里选） */
export const eduTeacherList = () => http.post('/wx_teacher/education/teacher_list', {}).then((r) => r.data)

/** 提交查课登记 */
export const eduAddRegister = (data) =>
  http.post('/wx_teacher/education/add_register', data).then((r) => r.data)

/** 追加一张登记照片（multipart：file + education_register_id） */
export const eduUploadPicture = (file, registerId) => {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('education_register_id', registerId)
  return http.post('/wx_teacher/education/add_picture', fd).then((r) => r.data)
}

/** 我的登记记录（分页，每页 10 条；keyword 可选，按班级/教师/课程/说明模糊搜索） */
export const eduRegisterList = (page, keyword, type) =>
  http.post('/wx_teacher/education/register_list', { page: page, keyword: keyword || '', type: type || '' }).then((r) => r.data)

/** 单条记录详情 */
export const eduGetRegister = (id) =>
  http.post('/wx_teacher/education/get_register', { id: id }).then((r) => r.data)

/** 修改登记（后端 get_register_edit 即 update） */
export const eduUpdateRegister = (data) =>
  http.post('/wx_teacher/education/get_register_edit', data).then((r) => r.data)

/** 删除登记（后端返回纯文本 success / error） */
export const eduRegisterDelete = (id) =>
  http.post('/wx_teacher/education/register_delete', { id: id }).then((r) => r.data)

export default http
